import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import FormData from "form-data";
import ScrollToTop from "react-scroll-to-top";
import ColorInit from "../helper/ColorInit";
import SduiHomeRenderer from "../components/sdui/SduiHomeRenderer";
import {
  getBannersList,
  getCategoriesList,
  getRecentlyBrowseProduct,
  getSegments,
  getStoreBrands,
  getStores,
} from "../redux/actions/action-creator";
import { selectConfigurationData } from "../redux/selectors/configurationSelectors";
import { buildScreenPayload } from "../utils/uiConfig";
import {
  DEFAULT_DELIVERY_LOCATION,
  LOCATION_STORAGE_KEY,
  getGuestLocationDisplayText,
} from "../utils/locationDefaults";

const DEFAULT_LOCATION = DEFAULT_DELIVERY_LOCATION;
const HOME_SEGMENT_TITLE = "GROCERY";
const SEGMENT_ID_KEYS = ["segment_id", "segmentId", "id"];
const BUSINESS_SEGMENT_KEYS = [
  "business_segment_id",
  "businessSegmentId",
  "business_segment",
  "businessSegment",
  "store_id",
  "storeId",
  "id",
  "branch_id",
];
const BANNER_CALLING_FROM = "category_screen";
const BANNER_SECTION_TYPES = ["hero_carousel", "hero_banner"];

const fallbackImage = (path) => `${process.env.PUBLIC_URL}/${path}`;

const normalizeImage = (image, fallbackPath) => {
  if (image && typeof image === "object") {
    return normalizeImage(
      image.desktop || image.tablet || image.mobile || image.url || image.src || image.path,
      fallbackPath
    );
  }

  const value = String(image || "").trim();

  if (!value) {
    return fallbackImage(fallbackPath);
  }

  if (/^(https?:|data:|blob:|\/)/i.test(value)) {
    return value;
  }

  return fallbackImage(value.replace(/^\/+/, ""));
};

const getValue = (item, keys, fallback = "") => {
  const matchedKey = keys.find(
    (key) => item?.[key] !== null && item?.[key] !== undefined && item?.[key] !== ""
  );

  return matchedKey ? item[matchedKey] : fallback;
};

const getCellContents = (data) => {
  const possibleLists = [
    data?.cell_contents,
    data?.response_data?.[0]?.cell_contents,
    data?.response_data,
    data?.data?.cell_contents,
    data?.data?.response_data?.[0]?.cell_contents,
    data?.data?.response_data,
    data?.banners,
    data?.banner_list,
    data?.bannerList,
    data?.items,
    data?.list,
    data?.data,
  ];

  return possibleLists.find((list) => Array.isArray(list)) || [];
};

const getResponseBlock = (data) =>
  data?.response_data?.[0] ||
  data?.data?.response_data?.[0] ||
  data?.data ||
  data ||
  null;

const getSectionTitle = (data, fallback) =>
  getValue(getResponseBlock(data), ["cell_title_text", "title", "name", "heading"], fallback);

const hasValidSegmentId = (id) =>
  id !== null && id !== undefined && id !== "" && Number(id) !== 0;

const isHomeSegment = (item) =>
  String(item?.title || item?.name || item?.segment_name || "")
    .trim()
    .toUpperCase() === HOME_SEGMENT_TITLE;

const getDynamicSegmentId = (segmentsData) => {
  const directSegmentId = getValue(segmentsData, SEGMENT_ID_KEYS, null);

  if (isHomeSegment(segmentsData) && hasValidSegmentId(directSegmentId)) {
    return directSegmentId;
  }

  const segmentItems = getCellContents(segmentsData);
  const homeSegment = segmentItems.find((item) => {
    const id = getValue(item, SEGMENT_ID_KEYS, null);
    return isHomeSegment(item) && hasValidSegmentId(id);
  });

  if (homeSegment) {
    return getValue(homeSegment, SEGMENT_ID_KEYS);
  }

  const selectedSegment = segmentItems.find((item) =>
    hasValidSegmentId(getValue(item, SEGMENT_ID_KEYS, null))
  );

  return getValue(selectedSegment, SEGMENT_ID_KEYS);
};

const getDynamicBusinessSegmentId = (storesData) => {
  const directBusinessSegmentId = getValue(storesData, BUSINESS_SEGMENT_KEYS);

  if (directBusinessSegmentId) {
    return directBusinessSegmentId;
  }

  const selectedStore = getCellContents(storesData).find((item) =>
    getValue(item, BUSINESS_SEGMENT_KEYS)
  );

  return getValue(selectedStore, BUSINESS_SEGMENT_KEYS);
};

const getDynamicSelectedStore = (storesData) => {
  const storeItems = getCellContents(storesData);

  return storeItems.find((item) => getValue(item, BUSINESS_SEGMENT_KEYS)) || storeItems[0] || null;
};

const appendHomeContextPayload = (formData, userLocation) => {
  formData.append("is_dev_stores_enable", 1);
  formData.append("latitude", userLocation.latitude);
  formData.append("longitude", userLocation.longitude);
  formData.append("merchant_id", userLocation.merchant_id);
  formData.append("country_id", userLocation.country_id);
  formData.append("home_screen", 1);
  formData.append("banner_for", 1);

  if (userLocation.area !== null && userLocation.area !== undefined && userLocation.area !== "") {
    formData.append("area", userLocation.area);
  }
};

const getStoreSegmentId = (storeItem, fallbackSegmentId = "") =>
  getValue(storeItem, ["segment_id", "segmentId", "segment"], fallbackSegmentId);

const buildBannerPayload = (segmentId, businessSegmentId) => {
  if (!hasValidSegmentId(segmentId) || !hasValidSegmentId(businessSegmentId)) return null;

  const params = new URLSearchParams();
  params.append("segment_id", segmentId);
  params.append("calling_from", BANNER_CALLING_FROM);
  params.append("business_segment_id", businessSegmentId);
  return params;
};

const slugify = (value, fallback = "item") =>
  String(value || fallback)
    .trim()
    .toLowerCase()
    .replace(/&/g, "and")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "") || fallback;

const toNumber = (value, fallback = 0) => {
  const numberValue = Number(String(value ?? "").replace(/[^0-9.]/g, ""));
  return Number.isFinite(numberValue) ? numberValue : fallback;
};

const getThemeIdForLabel = (themes = {}, item = {}, label = "") => {
  const directThemeId = getValue(item, ["theme_id", "themeId", "theme"]);
  const normalizedLabel = slugify(label, "");

  if (directThemeId && themes?.[directThemeId]) {
    return directThemeId;
  }

  return themes?.[normalizedLabel] ? normalizedLabel : undefined;
};

const getServiceIcon = (label = "") => {
  const normalizedLabel = label.toLowerCase();

  if (normalizedLabel.includes("food") || normalizedLabel.includes("restaurant")) return "Utensils";
  if (normalizedLabel.includes("medicine") || normalizedLabel.includes("pharmacy")) return "Pill";
  if (normalizedLabel.includes("taxi") || normalizedLabel.includes("cab")) return "CarTaxiFront";
  if (normalizedLabel.includes("water")) return "Droplets";
  if (normalizedLabel.includes("flower")) return "Flower2";
  if (normalizedLabel.includes("repair") || normalizedLabel.includes("handyman")) return "Wrench";
  if (normalizedLabel.includes("electronic") || normalizedLabel.includes("mobile")) return "Smartphone";
  return "ShoppingBag";
};

const getObjectText = (value, fallback = "") => {
  if (value && typeof value === "object") {
    return getValue(value, ["text", "label", "title", "name"], fallback);
  }

  return value || fallback;
};

const getColorStyle = (value = {}) => ({
  background: getValue(value, ["background", "backgroundColor", "background_color"]),
  color: getValue(value, ["text_color", "textColor", "color"]),
});

const normalizeBackendAction = (action = {}) => {
  const type = String(action.type || "").trim().toLowerCase();
  const id = action.id || action.category_id || action.categoryId || action.product_id || action.productId;
  const path = action.path || action.url || action.link;

  if (type === "navigate" && path) {
    return { type: "NAVIGATE", path };
  }

  if (type === "category" && id) {
    return { type: "NAVIGATE", path: `/shop/category-${encodeURIComponent(id)}` };
  }

  if ((type === "store" || type === "business_segment") && id) {
    return { type: "NAVIGATE", path: `/category/store-${encodeURIComponent(id)}` };
  }

  if ((type === "service" || type === "segment") && id) {
    return { type: "NAVIGATE", path: `/stores/${encodeURIComponent(id)}` };
  }

  if (type === "product") {
    return { type: "NAVIGATE", path: "/product-details-two" };
  }

  if (path && String(path).startsWith("/")) {
    return { type: "NAVIGATE", path };
  }

  return null;
};

const getBannerAction = (item) => {
  const buttonAction = normalizeBackendAction(item?.button?.action);
  if (buttonAction) return buttonAction;

  const itemAction = normalizeBackendAction(item?.action);
  if (itemAction) return itemAction;

  const internalPath = getValue(item, ["path", "redirect_path", "redirectPath", "link", "url"]);

  return {
    type: "NAVIGATE",
    path: String(internalPath || "").startsWith("/") ? internalPath : "/shop",
  };
};

const getBannerImageSet = (item) => {
  const image = item?.image || item?.images || {};
  const mobile = normalizeImage(image.mobile || item?.mobile_image || item?.mobileImage, "assets/images/thumbs/banner-img1.png");
  const tablet = normalizeImage(image.tablet || item?.tablet_image || item?.tabletImage || image.desktop, "assets/images/thumbs/banner-img1.png");
  const desktop = normalizeImage(image.desktop || item?.desktop_image || item?.desktopImage || image.url || image.src || item?.banner_image || item?.bannerImage || item?.image, "assets/images/thumbs/banner-img1.png");

  return {
    mobile,
    tablet,
    desktop,
    position: image.position || item?.image_position || item?.imagePosition || "right bottom",
  };
};

const mapBanners = (bannersData) =>
  getCellContents(bannersData).map((item, index) => {
    const badge = item?.badge || {};
    const discountBadge = item?.discount_badge || item?.discountBadge || {};
    const button = item?.button || {};
    const style = item?.style || {};
    const imageSet = getBannerImageSet(item);
    const title = getValue(
      item,
      ["title", "banner_title", "bannerTitle", "name", "heading", "main_text", "mainText"],
      "Fresh deals are live"
    );
    const subtitle = getValue(
      item,
      ["subtitle", "description", "sub_title", "subTitle", "text", "banner_text", "bannerText"],
      "Save more on every order"
    );
    const eyebrow = getObjectText(
      badge,
      getValue(item, ["eyebrow", "label", "tag", "offer_text", "offerText"], "Special offer")
    );
    const discountText = getObjectText(
      discountBadge,
      getValue(item, ["discount_text", "discountText", "coupon", "badge_text", "badgeText"], "")
    );
    const buttonLabel = getObjectText(
      button,
      getValue(item, ["button_text", "buttonText", "cta", "cta_text", "ctaText"], "Shop Now")
    );

    return {
      id: getValue(item, ["id", "banner_id", "bannerId"], `banner-${index}`),
      eyebrow,
      eyebrowStyle: getColorStyle(badge),
      title,
      subtitle,
      image: imageSet.desktop,
      imageSet,
      badge: discountText,
      discountBadge: {
        text: discountText,
        ...getColorStyle(discountBadge),
      },
      service: item?.service,
      status: item?.status,
      sort_order: item?.sort_order || item?.sortOrder,
      start_at: item?.start_at || item?.startAt,
      end_at: item?.end_at || item?.endAt,
      style: {
        background: getValue(style, ["background", "backgroundColor"]),
        gradient: style.gradient,
        titleColor: getValue(style, ["title_color", "titleColor"]),
        subtitleColor: getValue(style, ["subtitle_color", "subtitleColor"]),
        descColor: getValue(style, ["desc_color", "descColor"]),
      },
      primaryAction: {
        label: buttonLabel,
        style: getColorStyle(button),
        action: getBannerAction(item),
      },
    };
  });

const mapBannerOffers = (bannersData, payload) =>
  mapBanners(bannersData).map((item) => ({
    id: `offer-${item.id}`,
    label: item.eyebrow || "Offer",
    title: item.title,
    subtitle: item.subtitle,
    theme_id: payload.theme?.id,
    image: item.image,
    action: item.primaryAction?.action || { type: "NAVIGATE", path: "/shop" },
  }));

const mapServices = (servicesData, themes) =>
  getCellContents(servicesData).map((item, index) => {
    const label = getValue(item, ["name", "title", "segment_name", "segmentName"], `Service ${index + 1}`);
    const segmentId = getValue(item, SEGMENT_ID_KEYS, `service-${index}`);
    const themeId = getThemeIdForLabel(themes, item, label);

    return {
      id: String(segmentId),
      label,
      theme_id: themeId,
      icon: getServiceIcon(label),
      image: normalizeImage(getValue(item, ["image", "icon", "logo"]), "assets/images/category/category1.png"),
      bg: getValue(item, ["bg", "background", "background_color", "backgroundColor"]),
      action: hasValidSegmentId(segmentId)
        ? { type: "NAVIGATE", path: `/stores/${encodeURIComponent(segmentId)}` }
        : { type: "SHOW_MESSAGE", message: `${label} is coming soon` },
    };
  });

const getStoreTitle = (item, index) =>
  getValue(item, ["title", "name", "store_name", "storeName", "business_name", "businessName"], `Store ${index + 1}`);

const getStorePath = (item, index) => {
  const storeId = getValue(item, BUSINESS_SEGMENT_KEYS);
  const storeSlug = slugify(
    getValue(item, ["slug", "url_slug", "urlSlug", "segment_slug", "segmentSlug"]) ||
      getStoreTitle(item, index),
    "store"
  );

  return storeId ? `/category/${encodeURIComponent(`${storeSlug}-${storeId}`)}` : "/stores";
};

const mapStores = (storesData) =>
  getCellContents(storesData).map((item, index) => {
    const title = getStoreTitle(item, index);

    return {
      id: getValue(item, BUSINESS_SEGMENT_KEYS, `store-${index}`),
      title,
      subtitle: getValue(
        item,
        ["address", "store_address", "storeAddress", "location", "area_name", "areaName"],
        "Fresh picks and daily essentials"
      ),
      rating: getValue(item, ["rating", "avg_rating", "store_rating", "storeRating"], "4.5"),
      eta: getValue(
        item,
        ["time", "delivery_time", "deliveryTime", "estimated_delivery_time", "estimatedDeliveryTime"],
        "Delivery in 30-45 min"
      ),
      image: normalizeImage(
        getValue(item, [
          "background_image",
          "backgroundImage",
          "cover_image",
          "coverImage",
          "banner_image",
          "bannerImage",
          "store_image",
          "storeImage",
          "image",
        ]),
        "assets/images/thumbs/store-img2.png"
      ),
      action: { type: "NAVIGATE", path: getStorePath(item, index) },
    };
  });

const getCategoryTitle = (item, index) =>
  getValue(item, ["title", "name", "category_name", "categoryName"], `Category ${index + 1}`);

const mapCategories = (categoriesData, selectedStore, cartContext) => {
  const businessSegmentId =
    cartContext.business_segment_id || getValue(selectedStore, BUSINESS_SEGMENT_KEYS);
  const storeSlug = slugify(getValue(selectedStore, ["title", "name"], "store"), "store");

  return getCellContents(categoriesData).map((item, index) => {
    const title = getCategoryTitle(item, index);
    const categoryId = getValue(item, ["category_id", "categoryId", "id", "product_category_id"]);
    const categorySlug = `${slugify(title, "category")}${categoryId ? `-${categoryId}` : ""}`;
    const path = businessSegmentId
      ? `/shop/${encodeURIComponent(`${storeSlug}-${businessSegmentId}`)}/${encodeURIComponent(categorySlug)}`
      : `/shop/${encodeURIComponent(categorySlug)}`;

    return {
      id: categoryId || `category-${index}`,
      label: title,
      image: normalizeImage(
        getValue(item, ["image", "category_image", "categoryImage", "icon"]),
        "assets/images/thumbs/product-two-img1.png"
      ),
      action: { type: "NAVIGATE", path },
    };
  });
};

const mapRecentItems = (recentlyBrowseData) =>
  getCellContents(recentlyBrowseData).map((item, index) => {
    const title = getValue(item, ["title", "name", "product_name", "productTitle"], `Product ${index + 1}`);

    return {
      id: getValue(item, ["product_variant_id", "variant_id", "product_id", "id"], `recent-${index}`),
      title,
      subtitle: getValue(item, ["store_name", "storeName", "brand_name", "brandName", "subtitle"], "Recently viewed"),
      meta: getValue(item, ["weight_unit", "unit", "variant_name", "packing", "meta"], "Available now"),
      image: normalizeImage(
        getValue(item, ["image", "product_cover_image", "productCoverImage"]),
        "assets/images/thumbs/product-two-img1.png"
      ),
      action: { type: "NAVIGATE", path: "/product-details-two" },
    };
  });

const mapProducts = (recentlyBrowseData, cartContext) =>
  getCellContents(recentlyBrowseData).map((item, index) => {
    const id = String(
      getValue(item, ["product_variant_id", "variant_id", "product_id", "id"], `product-${index}`)
    );
    const name = getValue(item, ["title", "name", "product_name", "productTitle"], `Product ${index + 1}`);
    const price = toNumber(
      getValue(item, [
        "price",
        "product_price",
        "product_cost",
        "selling_price",
        "product_selling_price",
        "final_price",
        "discounted_price",
      ])
    );
    const mrp = toNumber(getValue(item, ["mrp", "selling_price", "product_selling_price"]), 0);

    return {
      id,
      name,
      subtitle: getValue(item, ["weight_unit", "unit", "variant_name", "packing"], "1 unit"),
      price,
      mrp: mrp && mrp !== price ? mrp : "",
      rating: getValue(item, ["rating", "avg_rating", "product_rating"], "4.5"),
      badge: getValue(item, ["badge", "tag", "label"], "Fresh"),
      image: normalizeImage(
        getValue(item, ["image", "product_cover_image", "productCoverImage"]),
        "assets/images/thumbs/product-two-img1.png"
      ),
      action: {
        type: "ADD_TO_CART",
        payload: {
          productId: id,
          segment_id: getValue(item, ["segment_id", "segmentId", "segment"], cartContext.segment_id),
          business_segment_id: getValue(
            item,
            ["business_segment_id", "businessSegmentId", "store_id", "storeId"],
            cartContext.business_segment_id
          ),
        },
      },
      detailAction: { type: "NAVIGATE", path: "/product-details-two" },
    };
  });

const mapBrands = (brandsData) =>
  getCellContents(brandsData).map((item, index) => {
    const id = getValue(item, ["id", "brand_id", "brandId"], `brand-${index}`);
    const label = getValue(item, ["name", "title", "brand_name", "brandName"], `Brand ${index + 1}`);

    return {
      id,
      label,
      image: normalizeImage(getValue(item, ["image", "brand_image", "brandImage", "logo"]), "assets/images/thumbs/brand-img1.png"),
      action: { type: "NAVIGATE", path: `/shop?brand_id=${encodeURIComponent(id)}` },
    };
  });

const getLiveDataForSection = (section, homeData, payload) => {
  const sectionKey = `${section.id || ""} ${section.componentType || ""} ${section.title || ""}`.toLowerCase();
  const hasBanners = getCellContents(homeData.banners).length > 0;
  const hasServices = getCellContents(homeData.services).length > 0;
  const hasStores = getCellContents(homeData.stores).length > 0;
  const hasCategories = getCellContents(homeData.categories).length > 0;
  const hasProducts = getCellContents(homeData.recentlybrowse).length > 0;
  const hasBrands = getCellContents(homeData.brands).length > 0;

  if (section.componentType === "hero_carousel" && hasBanners) {
    return { data: mapBanners(homeData.banners), title: section.title };
  }

  if (section.componentType === "hero_banner" && hasBanners) {
    return { data: mapBanners(homeData.banners)[0], title: section.title };
  }

  if (section.componentType === "service_grid" && hasServices) {
    return {
      data: mapServices(homeData.services, payload.themes),
      title: section.title || getSectionTitle(homeData.services, "Quick Services"),
    };
  }

  if (section.componentType === "store_rail" && hasStores) {
    return {
      data: mapStores(homeData.stores),
      title: section.title || getSectionTitle(homeData.stores, "Popular Near You"),
    };
  }

  if (section.componentType === "category_grid" && hasCategories) {
    return {
      data: mapCategories(homeData.categories, homeData.selectedStore, homeData.cartContext),
      title: section.title || getSectionTitle(homeData.categories, "Shop by Category"),
    };
  }

  if (section.componentType === "brand_rail" && hasBrands) {
    return {
      data: mapBrands(homeData.brands),
      title: section.title || getSectionTitle(homeData.brands, "Shop by Brands"),
    };
  }

  if ((section.componentType === "product_grid" || section.componentType === "product_rail") && hasProducts) {
    return {
      data: mapProducts(homeData.recentlybrowse, homeData.cartContext),
      title: section.title || getSectionTitle(homeData.recentlybrowse, "Recently Viewed"),
    };
  }

  if (section.componentType === "recent_rail" && hasProducts && !sectionKey.includes("store")) {
    return {
      data: mapRecentItems(homeData.recentlybrowse),
      title: section.title || getSectionTitle(homeData.recentlybrowse, "Recently Viewed"),
    };
  }

  if (section.componentType === "offer_cards" && hasBanners) {
    return {
      data: mapBannerOffers(homeData.banners, payload),
      title: section.title || getSectionTitle(homeData.banners, "Exclusive Offers"),
    };
  }

  return null;
};

const isBannerSection = (section) =>
  BANNER_SECTION_TYPES.includes(section?.componentType);

const appendMissingLiveSections = (sections, homeData) => {
  const nextSections = [...sections];
  const hasSectionType = (componentTypes) =>
    nextSections.some((section) => componentTypes.includes(section.componentType));

  if (getCellContents(homeData.banners).length > 0 && !hasSectionType(["hero_carousel", "hero_banner"])) {
    nextSections.unshift({
      id: "live_banners",
      componentType: "hero_carousel",
      layout: { mobile: 1, tablet: 1, laptop: 1, desktop: 1 },
      data: mapBanners(homeData.banners),
    });
  }

  if (getCellContents(homeData.categories).length > 0 && !hasSectionType(["category_grid"])) {
    nextSections.push({
      id: "live_categories",
      componentType: "category_grid",
      title: getSectionTitle(homeData.categories, "Shop by Category"),
      layout: { mobile: 3, tablet: 4, laptop: 6, desktop: 8 },
      data: mapCategories(homeData.categories, homeData.selectedStore, homeData.cartContext),
    });
  }

  if (getCellContents(homeData.recentlybrowse).length > 0 && !hasSectionType(["recent_rail", "product_grid", "product_rail"])) {
    nextSections.push({
      id: "live_recent_products",
      componentType: "product_rail",
      title: getSectionTitle(homeData.recentlybrowse, "Recently Viewed"),
      layout: { mobile: 2, tablet: 3, laptop: 5, desktop: 6 },
      data: mapProducts(homeData.recentlybrowse, homeData.cartContext),
    });
  }

  if (getCellContents(homeData.brands).length > 0 && !hasSectionType(["brand_rail"])) {
    nextSections.push({
      id: "live_brands",
      componentType: "brand_rail",
      title: getSectionTitle(homeData.brands, "Shop by Brands"),
      data: mapBrands(homeData.brands),
    });
  }

  return nextSections;
};

const enhanceSduiPayload = (payload, homeData, userLocation) => {
  const hasBanners = getCellContents(homeData.banners).length > 0;
  const sections = (payload.sections || [])
    .filter((section) => hasBanners || !isBannerSection(section))
    .map((section) => {
      const liveSection = getLiveDataForSection(section, homeData, payload);

      return liveSection
        ? {
            ...section,
            title: liveSection.title,
            data: liveSection.data,
          }
        : section;
    });

  return {
    ...payload,
    header: {
      ...payload.header,
      locationSubtitle:
        getGuestLocationDisplayText(
          userLocation,
          userLocation.area || payload.header?.locationSubtitle || "Select Location"
        ),
    },
    sections: appendMissingLiveSections(sections, homeData),
  };
};

const SduiHomePage = () => {
  const dispatch = useDispatch();
  const configurationData = useSelector(selectConfigurationData);
  const { latitude, longitude, user_location, country_id, area } = useSelector((state) => state.auth);
  const [homeData, setHomeData] = useState({
    services: null,
    stores: null,
    categories: null,
    selectedStore: null,
    recentlybrowse: null,
    brands: null,
    banners: null,
    cartContext: {},
  });

  const getSavedUserLocation = useCallback(() => {
    try {
      const savedLocation = localStorage.getItem(LOCATION_STORAGE_KEY);
      return savedLocation ? JSON.parse(savedLocation) : null;
    } catch (error) {
      return null;
    }
  }, []);

  const getActiveUserLocation = useCallback(() => {
    const savedLocation = getSavedUserLocation();
    const reduxLocation = user_location || {};

    return {
      latitude:
        latitude ??
        reduxLocation?.latitude ??
        savedLocation?.latitude ??
        DEFAULT_LOCATION.latitude,
      longitude:
        longitude ??
        reduxLocation?.longitude ??
        savedLocation?.longitude ??
        DEFAULT_LOCATION.longitude,
      country_id:
        country_id ??
        reduxLocation?.country_id ??
        reduxLocation?.countryId ??
        savedLocation?.country_id ??
        savedLocation?.countryId ??
        DEFAULT_LOCATION.country_id,
      merchant_id:
        reduxLocation?.merchant_id ??
        reduxLocation?.merchantId ??
        savedLocation?.merchant_id ??
        savedLocation?.merchantId ??
        DEFAULT_LOCATION.merchant_id,
      area:
        area ??
        reduxLocation?.area ??
        reduxLocation?.area_id ??
        savedLocation?.area ??
        savedLocation?.area_id,
      address:
        reduxLocation?.address ??
        savedLocation?.address ??
        reduxLocation?.location ??
        savedLocation?.location,
    };
  }, [area, country_id, getSavedUserLocation, latitude, longitude, user_location]);

  const userLocation = useMemo(() => getActiveUserLocation(), [getActiveUserLocation]);

  const loadSduiHomeData = useCallback(async () => {
    const activeLocation = getActiveUserLocation();
    let selectedSegmentId = "";
    let selectedBusinessSegmentId = "";
    let selectedStoreItem = null;
    let servicesData = null;
    let storesData = null;
    let categoriesData = null;
    let recentlybrowseData = null;
    let brandsData = null;
    let bannersData = null;

    const segmentData = new FormData();
    appendHomeContextPayload(segmentData, activeLocation);
    segmentData.append("segment_id", "");

    try {
      const segmentsResponse = await dispatch(getSegments(segmentData));
      if (segmentsResponse?.result) {
        servicesData = segmentsResponse.data;
        selectedSegmentId = getDynamicSegmentId(segmentsResponse.data) || "";
      }
    } catch (error) {
      console.log("SDUI segments API Error:", error?.message);
    }

    const storeData = new FormData();
    appendHomeContextPayload(storeData, activeLocation);
    storeData.append("segment_id", selectedSegmentId);

    try {
      const storesResponse = await dispatch(getStores(storeData));
      if (storesResponse?.result) {
        storesData = storesResponse.data;
        selectedStoreItem = getDynamicSelectedStore(storesResponse.data);
        selectedBusinessSegmentId =
          getValue(selectedStoreItem, BUSINESS_SEGMENT_KEYS) ||
          getDynamicBusinessSegmentId(storesResponse.data) ||
          "";
        selectedSegmentId = getStoreSegmentId(selectedStoreItem, selectedSegmentId);
      }
    } catch (error) {
      console.log("SDUI stores API Error:", error?.message);
    }

    const cartContext = {
      segment_id: selectedSegmentId,
      business_segment_id: selectedBusinessSegmentId,
    };

    if (selectedBusinessSegmentId) {
      const categoryData = new FormData();
      categoryData.append("business_segment_id", selectedBusinessSegmentId);
      categoryData.append("segment_id", selectedSegmentId);
      categoryData.append("calling_from", "website_screen");
      categoryData.append("is_website_calling", 1);

      try {
        const categoriesResponse = await dispatch(getCategoriesList(categoryData));
        categoriesData = categoriesResponse?.result ? categoriesResponse.data : null;
      } catch (error) {
        console.log("SDUI categories API Error:", error?.message);
      }
    }

    const recentProductData = new FormData();
    recentProductData.append("segment_id", selectedSegmentId);
    recentProductData.append("latitude", activeLocation.latitude);
    recentProductData.append("longitude", activeLocation.longitude);
    if (selectedBusinessSegmentId) {
      recentProductData.append("business_segment_id", selectedBusinessSegmentId);
    }

    const brandData = new FormData();
    brandData.append("segment_id", selectedSegmentId);
    brandData.append("merchant_id", activeLocation.merchant_id);
    brandData.append("calling_from", "category_screen");
    brandData.append("page", 1);
    brandData.append("per_page", 10);
    if (selectedBusinessSegmentId) {
      brandData.append("business_segment_id", selectedBusinessSegmentId);
    }

    const bannerPayload = buildBannerPayload(selectedSegmentId, selectedBusinessSegmentId);
    const bannerRequest = bannerPayload
      ? dispatch(getBannersList(bannerPayload))
          .then((response) => {
            bannersData = response?.result ? response.data : null;
          })
          .catch((error) => console.log("SDUI banners API Error:", error?.message))
      : Promise.resolve();

    await Promise.all([
      dispatch(getRecentlyBrowseProduct(recentProductData))
        .then((response) => {
          recentlybrowseData = response?.result ? response.data : null;
        })
        .catch((error) => console.log("SDUI recently browse API Error:", error?.message)),
      dispatch(getStoreBrands(brandData))
        .then((response) => {
          brandsData = response?.result ? response.data?.response_data?.[0] || response.data : null;
        })
        .catch((error) => console.log("SDUI brands API Error:", error?.message)),
      bannerRequest,
    ]);

    setHomeData({
      services: servicesData,
      stores: storesData,
      categories: categoriesData,
      selectedStore: selectedStoreItem,
      recentlybrowse: recentlybrowseData,
      brands: brandsData,
      banners: bannersData,
      cartContext,
    });
  }, [dispatch, getActiveUserLocation]);

  useEffect(() => {
    loadSduiHomeData();
  }, [loadSduiHomeData]);

  const basePayload = useMemo(
    () => buildScreenPayload(configurationData, "home"),
    [configurationData]
  );
  const payload = useMemo(
    () => enhanceSduiPayload(basePayload, homeData, userLocation),
    [basePayload, homeData, userLocation]
  );

  return (
    <>
      <ColorInit color={true} />
      <ScrollToTop smooth color={payload.theme?.colors?.primary || "#148f46"} />
      <SduiHomeRenderer payload={payload} />
    </>
  );
};

export default SduiHomePage;
